import React, { useState } from "react";
import { SocialAccountCard } from "./SocialAccountCard";
import { SiTiktok } from "react-icons/si";
import { PrimaryActionButton } from "@/components/ui/PrimaryActionButton";
import { Button } from "@/components/ui/button";
import { FaSpinner } from "react-icons/fa";
import { TikTokDataStatusDot } from "./TikTokDataStatusDot";
import { useTranslation } from "react-i18next";
import { useCachedTier } from '@/features/account/hooks/useCachedTier';
import { AnalyticsUpgradeModal } from './AnalyticsUpgradeModal';

interface TikTokAccountCardProps {
  account: {
    id: string;
    username?: string;
    name?: string;
    profile_picture_url?: string;
    connectionId: string;
    connectionType?: 'tiktok' | 'tiktok_business';
  };
  onDisconnect?: (connectionId: string) => void;
  onIndex?: (accountId: string, connectionId: string) => void;
  isIndexing?: Record<string, boolean>;
  currentMessageIndex?: Record<string, number>;
  indexingMessages?: string[];
}

export function TikTokAccountCard({ account, onDisconnect, onIndex, isIndexing = {}, currentMessageIndex = {}, indexingMessages = [] }: TikTokAccountCardProps) {
  const { t } = useTranslation();
  const handleDisconnect = () => {
    if (onDisconnect) onDisconnect(account.connectionId);
  };

  const profilePictureUrl = account.profile_picture_url || undefined;
  const loading = isIndexing[account.id] || false;
  const messageIdx = currentMessageIndex[account.id] || 0;
  const loadingText = indexingMessages[messageIdx] || undefined;
  const isBusiness = account.connectionType === 'tiktok_business';
  const { tier } = useCachedTier();
  const isPaidTier = tier !== null && tier.toLowerCase() !== 'free';
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleUpgrade = () => {
    setShowUpgradeModal(false);
    fetch('/api/stripe/create-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan: 'pro' }),
    })
      .then(async res => res.ok ? res.json() : null)
      .then(data => { if (data?.url) window.location.href = data.url; })
      .catch(err => console.error('[TikTokAccountCard] checkout error:', err));
  };

  const handleAnalytics = async () => {
    if (!isPaidTier) {
      setShowUpgradeModal(true);
      return;
    }
    setIsAnalyticsLoading(true);
    try {
      const response = await fetch('/api/analytics/unlock-analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectionId: account.connectionId, username: account.username ?? account.name }),
      });
      if (!response.ok) {
        throw new Error(`Failed to unlock analytics: ${response.statusText}`);
      }
    } catch (error) {
      console.error('[TikTokAccountCard] Analytics request failed:', error);
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  const accountTitle = account.name || account.username || (isBusiness ? 'TikTok Business Account' : 'TikTok User');

  console.log(`TikTok card ${account.id} state:`, {
    accountId: account.id,
    loading,
    messageIdx,
    loadingText,
    isBusiness,
    connectionType: account.connectionType,
    showButton: Boolean(onIndex),
    isIndexingValue: isIndexing[account.id],
    isIndexingKeys: Object.keys(isIndexing)
  });

  return (
    <>
      <SocialAccountCard
      title={accountTitle}
      username={account.username}
      profilePictureUrl={profilePictureUrl}
      icon={<SiTiktok />}
      onDisconnect={onDisconnect ? handleDisconnect : undefined}
      statusDot={<TikTokDataStatusDot connectionIds={[account.connectionId]} />}
      badge={isBusiness ? 'Business' : undefined}
    >
      {onIndex && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <Button
            className="w-full sm:flex-1 min-h-10 h-auto whitespace-normal text-center leading-tight py-2"
            variant="highlight"
            onClick={() => onIndex(account.id, account.connectionId)}
            disabled={loading}
            aria-label={t('account_social.update_ai_aria')}
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" />
                {loadingText}
              </>
            ) : (
              <>{t('account_social.update_ai')}</>
            )}
          </Button>
          <Button
            className="w-full sm:flex-1 min-h-10 h-auto whitespace-normal text-center leading-tight py-2"
            variant="secondary"
            onClick={handleAnalytics}
            disabled={isAnalyticsLoading}
            aria-label={t('account_social.unlock_analytics_aria')}
          >
            {isAnalyticsLoading ? (
              <>
                <FaSpinner className="animate-spin" />
                {t('account_social.unlock_analytics_loading')}
              </>
            ) : isPaidTier ? (
              t('account_social.unlock_analytics')
            ) : (
              t('account_social.unlock_analytics_locked')
            )}
          </Button>
        </div>
      )}
    </SocialAccountCard>
      <AnalyticsUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
      />
    </>
  );
}
